import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { protocolosKeys } from '@/features/protocolos/hooks/useProtocolos';
import type { EditarCaptionVars, EliminarFotoVars } from '@/features/analisis/common/fotosTypes';

import { ectoparasitosApi } from '../api';
import type { Ectoparasitos, EctoparasitosCreate, EctoparasitosUpdate } from '../types';

const QUERY_KEY_BASE = ['analisis-ectoparasitos'] as const;

export const ectoparasitosKeys = {
  all: QUERY_KEY_BASE,
  byProtocolo: (uidProtocolo: string) =>
    [...QUERY_KEY_BASE, 'by-protocolo', uidProtocolo] as const,
  detail: (uidProtocolo: string, uidAnalisis: string) =>
    [...QUERY_KEY_BASE, uidProtocolo, uidAnalisis] as const,
};

export function useEctoparasitosListByProtocolo(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo
      ? ectoparasitosKeys.byProtocolo(uidProtocolo)
      : ectoparasitosKeys.all,
    queryFn: ({ signal }) => ectoparasitosApi.listByProtocolo(uidProtocolo!, signal),
    enabled: !!uidProtocolo,
  });
}

export function useEctoparasitos(
  uidProtocolo: string | undefined,
  uidAnalisis: string | undefined
) {
  return useQuery({
    queryKey:
      uidProtocolo && uidAnalisis
        ? ectoparasitosKeys.detail(uidProtocolo, uidAnalisis)
        : ectoparasitosKeys.all,
    queryFn: ({ signal }) => ectoparasitosApi.getById(uidProtocolo!, uidAnalisis!, signal),
    enabled: !!(uidProtocolo && uidAnalisis),
  });
}

export function useCreateEctoparasitos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      data,
    }: {
      uidProtocolo: string;
      data: EctoparasitosCreate;
    }) => ectoparasitosApi.create(uidProtocolo, data),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({
        queryKey: ectoparasitosKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}

export function useUpdateEctoparasitos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      data,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      data: EctoparasitosUpdate;
    }) => ectoparasitosApi.update(uidProtocolo, uidAnalisis, data),
    onSuccess: (updated: Ectoparasitos, { uidProtocolo, uidAnalisis }) => {
      void queryClient.invalidateQueries({
        queryKey: ectoparasitosKeys.byProtocolo(uidProtocolo),
      });
      queryClient.setQueryData(ectoparasitosKeys.detail(uidProtocolo, uidAnalisis), updated);
    },
  });
}

export function useDeleteEctoparasitos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
    }) => ectoparasitosApi.remove(uidProtocolo, uidAnalisis),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({
        queryKey: ectoparasitosKeys.byProtocolo(uidProtocolo),
      });
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.detail(uidProtocolo) });
    },
  });
}

export function useUploadFotoEctoparasitos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      file,
      caption,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      file: File;
      caption?: string | null;
    }) => ectoparasitosApi.uploadFoto(uidProtocolo, uidAnalisis, file, caption ?? null),
    onSuccess: (updated: Ectoparasitos, { uidProtocolo, uidAnalisis }) => {
      queryClient.setQueryData(ectoparasitosKeys.detail(uidProtocolo, uidAnalisis), updated);
      void queryClient.invalidateQueries({
        queryKey: ectoparasitosKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}

export function useEliminarFotoEctoparasitos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uidProtocolo, uidAnalisis, uid_foto }: EliminarFotoVars) =>
      ectoparasitosApi.eliminarFoto(uidProtocolo, uidAnalisis, uid_foto),
    onSuccess: (updated: Ectoparasitos, { uidProtocolo, uidAnalisis }) => {
      queryClient.setQueryData(ectoparasitosKeys.detail(uidProtocolo, uidAnalisis), updated);
      void queryClient.invalidateQueries({
        queryKey: ectoparasitosKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}

export function useEditarCaptionEctoparasitos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uidProtocolo, uidAnalisis, uid_foto, caption }: EditarCaptionVars) =>
      ectoparasitosApi.editarCaption(uidProtocolo, uidAnalisis, uid_foto, caption),
    onSuccess: (updated: Ectoparasitos, { uidProtocolo, uidAnalisis }) => {
      queryClient.setQueryData(ectoparasitosKeys.detail(uidProtocolo, uidAnalisis), updated);
      void queryClient.invalidateQueries({
        queryKey: ectoparasitosKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}
